import { Component, inject, signal } from '@angular/core';
import {
  ButtonClickEvent,
  CacBaseDialogComponent,
  CacDialogLayoutComponent,
} from '@controladad/ng-base';
import { formControl, Validators, formGroup, FormControlExtended, FormGroupExtended } from '@al00x/forms';
import { OrderApiService, AdminProductsApiService } from '../../../services';
import { ProductEntity, VariantEntity, ProductDropdownItem } from '@models';
import { LazyDropdownComponent, LazyDropdownItem } from '../../../components';
import { MatIconModule } from '@angular/material/icon';
import { ReactiveFormsModule } from '@angular/forms';
import { KwdCurrencyPipe } from '../../../pipes';

declare const snackbar$: any;

export interface OrderItemAddDialogData {
  orderId: number;
  existingVariantIds?: number[];
}

export type OrderItemAddDialogResult = boolean;

export type OrderItemAddForm = {
  productId: FormControlExtended<number | null>;
  variantId: FormControlExtended<number | null>;
  quantity: FormControlExtended<number>;
};

@Component({
  selector: 'lib-order-item-add-dialog',
  standalone: true,
  imports: [
    CacDialogLayoutComponent,
    LazyDropdownComponent,
    MatIconModule,
    ReactiveFormsModule,
    KwdCurrencyPipe,
  ],
  templateUrl: './order-item-add-dialog.component.html',
  styleUrl: './order-item-add-dialog.component.scss',
})
export class OrderItemAddDialogComponent extends CacBaseDialogComponent<
  OrderItemAddDialogData,
  OrderItemAddDialogResult
> {
  private readonly orderApi = inject(OrderApiService);
  private readonly adminProductApi = inject(AdminProductsApiService);

  // State management
  selectedProduct = signal<ProductEntity | null>(null);
  selectedVariant = signal<VariantEntity | null>(null);
  loadingProduct = signal<boolean>(false);
  isProductOutOfStock = signal<boolean>(false);

  // Product dropdown configuration
  productApiAdapter = this.adminProductApi.getProductNamesAdapter();
  productItemMapper = (product: ProductDropdownItem): LazyDropdownItem => ({
    id: product.id,
    label: product.name.english || product.name.arabic || 'Unnamed Product',
    value: product.id,
    subtitle: product.name.arabic && product.name.english !== product.name.arabic ? product.name.arabic : undefined,
  });

  // Form
  form = formGroup<OrderItemAddForm>({
    productId: formControl<number | null>(null, [Validators.required]),
    variantId: formControl<number | null>(null),
    quantity: formControl<number>({ value: 1, disabled: true }, [Validators.required, Validators.min(1)]),
  });

  constructor() {
    super();
    this.setupFormListeners();
  }

  private setupFormListeners(): void {
    // No quantity validation - admin can add any quantity
  }

  onProductSelectionChange(selectedProduct: LazyDropdownItem | LazyDropdownItem[] | null): void {
    const product = Array.isArray(selectedProduct) ? selectedProduct[0] : selectedProduct;

    // Reset state
    this.selectedProduct.set(null);
    this.selectedVariant.set(null);
    this.isProductOutOfStock.set(false);
    this.form.patchValue({
      productId: null,
      variantId: null,
      quantity: 1,
    });
    this.form.controls.quantity.disable();

    if (!product) {
      return;
    }

    this.form.patchValue({ productId: product.id });
    this.loadProductDetails(product.id);
  }

  private loadProductDetails(productId: number): void {
    this.loadingProduct.set(true);

    this.adminProductApi.get(productId).subscribe({
      next: (product) => {
        this.selectedProduct.set(product);
        this.isProductOutOfStock.set(false);
        this.loadingProduct.set(false);

        // Simple and bundle products expose one sellable variant.
        if (product.type !== 1 && product.variants.length > 0) {
          const variant = product.variants[0];
          this.onVariantSelected(variant);
        } else if (product.type === 1) {
          // For variable products, wait for user to select variant
          this.form.controls.quantity.disable();
        }
      },
      error: (error) => {
        console.error('Error loading product details:', error);
        snackbar$.show('Error loading product details.');
        this.loadingProduct.set(false);
        this.isProductOutOfStock.set(true);
        this.form.controls.quantity.disable();
      },
    });
  }

  getAvailableVariants(product: ProductEntity | null = null): VariantEntity[] {
    const currentProduct = product || this.selectedProduct();
    if (!currentProduct || currentProduct.type !== 1) {
      return [];
    }

    // Return all variants - admin can add any variant including duplicates
    return currentProduct.variants;
  }

  onVariantSelectionChange(variantId: number): void {
    const product = this.selectedProduct();
    if (!product) return;

    const variant = product.variants.find(v => v.id === variantId);
    if (!variant) {
      this.selectedVariant.set(null);
      this.form.patchValue({ variantId: null });
      this.form.controls.quantity.disable();
      return;
    }

    this.onVariantSelected(variant);
  }

  private onVariantSelected(variant: VariantEntity): void {
    this.selectedVariant.set(variant);
    this.form.patchValue({ variantId: variant.id });

    // Update quantity validators
    this.updateQuantityValidation();

    // Enable quantity control
    this.form.controls.quantity.enable();
  }

  private updateQuantityValidation(): void {
    const quantityControl = this.form.controls.quantity;

    // Admin can add any quantity regardless of stock
    quantityControl.setValidators([Validators.required, Validators.min(1)]);
    quantityControl.updateValueAndValidity();
  }

  getVariantDisplayText(variant: VariantEntity): string {
    if (!variant.attributes || variant.attributes.length === 0) {
      return 'Default';
    }

    return variant.attributes
      .map(attr => `${attr.attributeName.english || attr.attributeName.arabic}-${attr.term.english || attr.term.arabic}`)
      .join(' - ');
  }

  getItemPrice(): number {
    const variant = this.selectedVariant();
    if (!variant) return 0;
    return variant.salePrice || variant.regularPrice;
  }

  getTotalPrice(): number {
    const itemPrice = this.getItemPrice();
    const quantity = this.form.controls.quantity.value || 1;
    return itemPrice * quantity;
  }

  onSubmit(e: ButtonClickEvent): void {
    const value = this.form.value;

    if (!value || !value.variantId) {
      snackbar$.show('Please select a product and variant');
      e.setLoading(false);
      return;
    }

    this.addOrderItemRequest(value, e, false);
  }

  private addOrderItemRequest(value: any, e: ButtonClickEvent, forceAdd: boolean): void {
    const request = this.orderApi.addOrderItem({
      orderId: this.data.orderId,
      variantId: value.variantId,
      quantity: value.quantity!,
      variantOrderState: 1,
      forceAdd: forceAdd,
    });

    request.pipe(e.pipe()).subscribe({
      next: () => {
        snackbar$.show('Product added to order successfully');
        this.submit(true);
      },
      error: (error) => {
        // Check if error code is 78 (stock not available)
        const errorCode = error?.error?.code || error?.code;
        if (errorCode === 78 && !forceAdd) {
          dialog$.prompt({
            title: 'Stock Not Available',
            message: 'The requested quantity exceeds available stock. Do you want to proceed anyway? This will result in negative stock.',
            yesButtonText: 'Yes, Proceed',
            noButtonText: 'Cancel',
        }).action(() =>
            this.orderApi.addOrderItem({
              orderId: this.data.orderId,
              variantId: value.variantId,
              quantity: value.quantity!,
              variantOrderState: 1,
              forceAdd: true,
            })
          ).subscribe({
            next: () => {
              snackbar$.show('Product added to order successfully');
              this.submit(true);
            },
            error: () => {
              e.setLoading(false);
            }
          });
        } else {
          e.setLoading(false);
        }
      }
    });
  }
}
